// Noun declension, ported one-to-one from tools/generate_paradigms.py (decl, add_alt, build).
// Two numbers only; eight case positions; चतुर्थी = षष्ठी (नि.४६).

import type {
    Case,
    CaseTable,
    Cell,
    Gender,
    Num,
    NounEntry
} from '@/prakrit/lib/grammar/types'

import {
    finalVowel,
    lengthen,
    setA,
    shorten
} from '@/prakrit/lib/grammar/devanagari'
import { F } from '@/prakrit/lib/grammar/verbs'
import { CASES } from '@/prakrit/lib/grammar/types'
import { NOUNS, RAYA, SOURCE } from '@/prakrit/lib/grammar/lexicon'

type Pair = [eka: string, bahu: string]

export const decl = (stem: string, gender: Gender): { table: CaseTable, rules: string } => {
    const v = finalVowel(stem)
    const L = lengthen(stem)
    const S = shorten(stem)
    let R: Record<Case, Pair>
    let rules: string

    if ((gender === 'pu' || gender === 'napum') && v === 'a') {
        const e = setA(stem, 'ए')

        R = {
            prathama: gender === 'napum' ? [stem + 'ं', L + 'णि'] : [setA(stem, 'ओ'), L],
            dvitiya: gender === 'napum' ? [stem + 'ं', L + 'णि'] : [stem + 'ं', L],
            tritiya: [e + 'ण', e + 'हि'],
            chaturthi: [stem + 'स्स', L + 'ण'],
            panchami: [stem + 'त्तो', L + 'हिंतो'],
            shashthi: [stem + 'स्स', L + 'ण'],
            saptami: [e, e + 'सु'],
            sambodhan: gender === 'napum' ? [stem, L + 'णि'] : [setA(stem, 'ओ'), L]
        }
        rules = 'नि.२०, २८, ३२, ३७, ४३, ४७, ५२–५३, ५७, ५९'
    }
    else if ((gender === 'pu' || gender === 'napum') && (v === 'i' || v === 'u')) {
        R = {
            prathama: gender === 'napum' ? [stem + 'ं', L + 'णि'] : [L, stem + 'णो'],
            dvitiya: gender === 'napum' ? [stem + 'ं', L + 'णि'] : [stem + 'ं', stem + 'णो'],
            tritiya: [stem + 'णा', L + 'हि'],
            chaturthi: [stem + 'णो', L + 'ण'],
            panchami: [stem + 'त्तो', L + 'हिंतो'],
            shashthi: [stem + 'णो', L + 'ण'],
            saptami: [stem + 'म्मि', L + 'सु'],
            sambodhan: gender === 'napum' ? [stem, L + 'णि'] : [L, stem + 'णो']
        }
        rules = 'नि.२१–२२, २८, ३२, ३७, ४३, ४७, ५२–५३, ५७, ५९'
    }
    else if (gender === 'stri') {
        const nomSg = v === 'ā' || v === 'ī' || v === 'ū' ? stem : L
        let dat: string

        if (v === 'ā') dat = stem + 'अ'
        else if (v === 'i' || v === 'ī') dat = L + 'आ'
        else dat = L + 'ए'

        const vocSg = v === 'ā' ? stem : S

        R = {
            prathama: [nomSg, L + 'ओ'],
            dvitiya: [S + 'ं', L + 'ओ'],
            tritiya: [L + 'ए', L + 'हि'],
            chaturthi: [dat, L + 'ण'],
            panchami: [S + 'त्तो', L + 'हिंतो'],
            shashthi: [dat, L + 'ण'],
            saptami: [L + 'ए', L + 'सु'],
            sambodhan: [vocSg, L + 'ओ']
        }
        rules = 'नि.२३–२५, २९, ३३, ३९–४०, ४४, ४८, ५४–५५, ५८'
    }
    else {
        throw new Error(`Cannot decline ${stem} (${gender})`)
    }

    const out = {} as CaseTable

    for (const c of CASES) {
        out[c] = { eka: [F(R[c][0])], bahu: [F(R[c][1])] } as Record<Num, Cell>
    }

    return { table: out, rules }
}

export const addAlt = (t: CaseTable, c: Case, n: Num, form: string, note = 'पाठ ८९'): void => {
    t[c][n].push(F(form, 'alt', note))
}

/** One noun entry exactly as data/nouns.json carries it, including the kit's विकल्प रूप. */
export const buildNoun = (stem: string, gender: Gender, hi: string, en: string): NounEntry => {
    const { table: t, rules } = decl(stem, gender)

    if (stem === 'पुरिस' || (finalVowel(stem) === 'a' && gender === 'pu')) {
        addAlt(t, 'tritiya', 'eka', setA(stem, 'ए') + 'णं')
        addAlt(t, 'panchami', 'eka', setA(stem, 'आ') + 'ओ')
    }

    if (gender === 'napum' && finalVowel(stem) === 'a') {
        addAlt(t, 'prathama', 'bahu', lengthen(stem) + 'इं')
        addAlt(t, 'dvitiya', 'bahu', lengthen(stem) + 'इं')
    }

    return { stem, gender, stem_class: finalVowel(stem), hi, en, rules, source: SOURCE, forms: t }
}

/** राय (राजा) — नि.६४: declined like पुरिस plus its विकल्प रूप. */
export const buildRaya = (): NounEntry => {
    const { table: raya } = decl(RAYA.stem, 'pu')
    const alts: [Case, string, string][] = [
        ['prathama', 'राया', 'राइणो'],
        ['dvitiya', 'राइणं', 'राइणो'],
        ['tritiya', 'राइणा', 'राईहि'],
        ['chaturthi', 'राइणो', 'राईण'],
        ['panchami', 'राइणो', 'राईहिंतो'],
        ['shashthi', 'राइणो', 'राईण'],
        ['saptami', 'राइम्मि', 'राईसु'],
        ['sambodhan', 'राया', 'राइणो']
    ]

    for (const [c, e, b] of alts) {
        addAlt(raya, c, 'eka', e, 'नि.६४')
        addAlt(raya, c, 'bahu', b, 'नि.६४')
    }

    return {
        stem: RAYA.stem,
        gender: 'pu',
        stem_class: 'a',
        hi: RAYA.hi,
        en: RAYA.en,
        rules: RAYA.rules,
        source: SOURCE,
        forms: raya
    }
}

/** Every kit noun in data/nouns.json order (NOUNS, then राय). */
export const buildKitNouns = (): NounEntry[] => [
    ...NOUNS.map(([stem, gender, hi, en]) => buildNoun(stem, gender, hi, en)),
    buildRaya()
]
